import React, { useEffect } from 'react';
import ImageCompareSlider from '../ImageCompareSlider';
import { formatSize } from '../../utils/imageUtils';

const MODAL_ID = 'compare-modal';

/**
 * Shared image comparison modal — uses <s-modal> (Shopify native).
 * Used by OptimizedImagesManagement, ManualUploadPage, and ImageCrushPage.
 */
function CompareModal({ data, onClose }) {
  useEffect(() => {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    if (data) {
      const t = setTimeout(() => modal.showOverlay?.(), 50);
      return () => clearTimeout(t);
    }
    modal.hideOverlay?.();
  }, [data]);

  const handleViewFullscreen = () => {
    const params = new URLSearchParams({
      original:      data.originalUrl,
      optimized:     data.optimizedUrl,
      name:          data.originalName || 'Image Comparison',
      originalSize:  (data.originalSizeKb  ?? 0).toString(),
      optimizedSize: (data.optimizedSizeKb ?? 0).toString(),
      savings:       (data.savingsPercent  ?? 0).toString(),
    });
    window.open(`/public/image-compare?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <s-modal
      id={MODAL_ID}
      heading={data?.originalName || 'Image Comparison'}
      size="large"
    >
      {data && (
        <s-stack style={{ maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          <div
            style={{
              flexShrink: 0,
              paddingBottom: '.5rem',
              borderBottom: '1px solid var(--p-color-border-subdued, #E1E3E5)',
            }}
          >
            <s-stack direction="inline" gap="base" justifyContent="space-between" alignItems="center" flexWrap="wrap">
              <s-stack direction="inline" gap="small-200" alignItems="center" flexWrap="wrap">
                <s-badge>Original: {formatSize(data.originalSizeKb)}</s-badge>
                <s-badge tone="success">Optimized: {formatSize(data.optimizedSizeKb)}</s-badge>
                <s-badge tone="info">Savings: {Number(data.savingsPercent ?? 0).toFixed(1)}%</s-badge>
              </s-stack>
              <s-stack direction="inline" gap="small-200" alignItems="center">
                <s-button variant="secondary" icon="view" onClick={handleViewFullscreen}>
                  View Fullscreen
                </s-button>
                <s-button variant="secondary" onClick={onClose}>Close</s-button>
              </s-stack>
            </s-stack>
          </div>

          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 0,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                minHeight: '400px',
                maxHeight: '70vh',
                position: 'relative',
              }}
            >
              <ImageCompareSlider
                key={data.originalUrl}
                originalImageUrl={data.originalUrl}
                optimizedImageUrl={data.optimizedUrl}
                imageName={data.originalName}
                originalSize={data.originalSizeKb}
                optimizedSize={data.optimizedSizeKb}
                savingsPercent={data.savingsPercent}
              />
            </div>
          </div>

        </s-stack>
      )}
    </s-modal>
  );
}

export default CompareModal;
